import { useEffect, useState, useCallback } from "react";
import { Plus, Search, Pencil, Ban, CheckCircle2, Trash2, Globe, UserCog } from "lucide-react";
import { toast } from "sonner";
import api, { fmtINR, fmtDate, errMsg } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { PageHeader, StatusBadge, TableShell, TR, TD, LoadingRows, EmptyState, ConfirmDialog } from "../../components/Shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const EMPTY = { name: "", email: "", password: "", company: "", phone: "" };

export default function AdminPartners() {
  const [partners, setPartners] = useState(null);
  const [q, setQ] = useState("");
  const [dialog, setDialog] = useState(null); // {mode:'create'|'edit'|'websites', partner}
  const [form, setForm] = useState(EMPTY);
  const [websites, setWebsites] = useState([]);
  const [busy, setBusy] = useState(false);
  const [admins, setAdmins] = useState([]);
  const { user } = useAuth();
  const isRoot = user?.role === "ROOT_SUPER_ADMIN";

  const load = useCallback(() => {
    api.get("/admin/partners", { params: { q } }).then((r) => setPartners(r.data));
  }, [q]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (isRoot) api.get("/root/admins").then((r) => setAdmins(r.data.admins)).catch(() => {}); }, [isRoot]);

  const reassign = async (p, adminId) => {
    try {
      await api.patch(`/root/partners/${p.id}`, { admin_id: adminId });
      toast.success(adminId ? `Assigned ${p.name} to the selected Super Admin` : `${p.name} unassigned (Root-only)`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (dialog.mode === "create") {
        const { data } = await api.post("/admin/partners", form);
        toast.success(`Partner created. Temporary password: ${data.temporary_password}`, { duration: 10000 });
      } else {
        const payload = { name: form.name, company: form.company, phone: form.phone };
        if (form.password) payload.password = form.password;
        await api.patch(`/admin/partners/${dialog.partner.id}`, payload);
        toast.success("Partner updated");
      }
      setDialog(null); load();
    } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };

  const setStatus = async (p, status) => {
    await api.patch(`/admin/partners/${p.id}`, { status });
    toast.success(`Partner ${status === "active" ? "activated" : "suspended"}`);
    load();
  };

  const remove = async (p) => {
    await api.delete(`/admin/partners/${p.id}`);
    toast.success("Partner deleted — their websites were suspended");
    load();
  };

  const viewWebsites = async (p) => {
    const { data } = await api.get(`/admin/partners/${p.id}/websites`);
    setWebsites(data);
    setDialog({ mode: "websites", partner: p });
  };

  return (
    <div data-testid="admin-partners">
      <PageHeader title="Partners" subtitle="Create, manage and monitor platform partners.">
        <Button data-testid="create-partner-button" onClick={() => { setForm(EMPTY); setDialog({ mode: "create" }); }}
          className="rounded-full font-bold active:scale-95 transition-transform">
          <Plus className="h-4 w-4 mr-2" /> Create Partner
        </Button>
      </PageHeader>

      <div className="relative max-w-sm mb-6">
        <Search className="h-4 w-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input data-testid="partner-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search partners…" className="pl-10 h-11 rounded-full" />
      </div>

      {!partners ? <LoadingRows /> : partners.length === 0 ? (
        <EmptyState title="No partners found" body="Create your first partner to get started." />
      ) : (
        <TableShell testid="partners-table" head={["Partner", "Contact", "Websites", "Monthly Billing", "Subscription", "Status", "Actions"]}>
          {partners.map((p) => (
            <TR key={p.id} testid={`partner-row-${p.id}`}>
              <TD>
                <p className="font-bold">{p.name}</p>
                <p className="text-xs text-muted-foreground">{p.company || "—"}</p>
              </TD>
              <TD><p className="text-sm">{p.email}</p><p className="text-xs text-muted-foreground">{p.phone || "—"}</p></TD>
              <TD><span className="font-display font-bold">{p.active_websites}</span><span className="text-muted-foreground text-xs"> / {p.total_websites} active</span></TD>
              <TD className="font-bold">{fmtINR(p.monthly_amount)}<span className="text-xs text-muted-foreground font-normal">/mo</span></TD>
              <TD><StatusBadge status={p.subscription_status || "trial"} /></TD>
              <TD><StatusBadge status={p.status} /></TD>
              <TD>
                <div className="flex items-center gap-1">
                  {isRoot && (
                    <select data-testid={`partner-assign-${p.id}`} value={p.admin_id || ""} onChange={(e) => reassign(p, e.target.value)} title="Assign to Super Admin"
                      className="h-8 max-w-32 rounded-full border border-input bg-background px-2 text-[11px] font-bold">
                      <option value="">Unassigned (Root)</option>
                      {admins.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                    </select>
                  )}
                  <button data-testid={`partner-websites-${p.id}`} onClick={() => viewWebsites(p)} title="View websites" className="h-8 w-8 rounded-full hover:bg-muted flex items-center justify-center transition-colors"><Globe className="h-4 w-4" /></button>
                  <button data-testid={`partner-edit-${p.id}`} onClick={() => { setForm({ name: p.name, email: p.email, company: p.company || "", phone: p.phone || "", password: "" }); setDialog({ mode: "edit", partner: p }); }} title="Edit" className="h-8 w-8 rounded-full hover:bg-muted flex items-center justify-center transition-colors"><Pencil className="h-4 w-4" /></button>
                  {p.status === "active" ? (
                    <button data-testid={`partner-suspend-${p.id}`} onClick={() => setStatus(p, "suspended")} title="Suspend" className="h-8 w-8 rounded-full hover:bg-amber-500/10 text-amber-600 flex items-center justify-center transition-colors"><Ban className="h-4 w-4" /></button>
                  ) : (
                    <button data-testid={`partner-activate-${p.id}`} onClick={() => setStatus(p, "active")} title="Activate" className="h-8 w-8 rounded-full hover:bg-emerald-500/10 text-emerald-600 flex items-center justify-center transition-colors"><CheckCircle2 className="h-4 w-4" /></button>
                  )}
                  <ConfirmDialog testid={`partner-delete-${p.id}`} title="Delete partner?" description={`This deletes ${p.name} and suspends all their websites. Store data is preserved but inaccessible.`}
                    onConfirm={() => remove(p)}
                    trigger={<button title="Delete" className="h-8 w-8 rounded-full hover:bg-destructive/10 text-destructive flex items-center justify-center transition-colors"><Trash2 className="h-4 w-4" /></button>} />
                </div>
              </TD>
            </TR>
          ))}
        </TableShell>
      )}

      <Dialog open={dialog?.mode === "create" || dialog?.mode === "edit"} onOpenChange={() => setDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-2xl">{dialog?.mode === "create" ? "Create Partner" : "Edit Partner"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4 mt-2" data-testid="partner-form">
            <div className="space-y-1.5"><Label className="overline-label">Full name</Label>
              <Input data-testid="partner-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label className="overline-label">Email</Label>
              <Input data-testid="partner-email" type="email" required disabled={dialog?.mode === "edit"} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="overline-label">Company</Label>
                <Input data-testid="partner-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} /></div>
              <div className="space-y-1.5"><Label className="overline-label">Phone</Label>
                <Input data-testid="partner-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label className="overline-label">{dialog?.mode === "create" ? "Password (blank = auto-generate)" : "New password (blank = keep current)"}</Label>
              <Input data-testid="partner-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
            {isRoot && dialog?.mode === "create" && (
              <div className="space-y-1.5"><Label className="overline-label">Assign to Super Admin</Label>
                <select data-testid="partner-admin-assign" value={form.admin_id || ""} onChange={(e) => setForm({ ...form, admin_id: e.target.value || null })}
                  className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm">
                  <option value="">Unassigned (Root-only workspace)</option>
                  {admins.map((a) => <option key={a.id} value={a.id}>{a.name} — {a.email}</option>)}
                </select>
              </div>
            )}
            <Button data-testid="partner-save-button" disabled={busy} className="w-full h-11 rounded-full font-bold">{busy ? "Saving…" : "Save Partner"}</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog?.mode === "websites"} onOpenChange={() => setDialog(null)}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader><DialogTitle className="font-display text-2xl">{dialog?.partner?.name}'s Websites</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2 max-h-96 overflow-y-auto">
            {websites.length === 0 && <p className="text-sm text-muted-foreground py-8 text-center">No websites yet</p>}
            {websites.map((w) => (
              <div key={w.id} data-testid={`partner-website-${w.id}`} className="flex items-center justify-between border border-border rounded-md p-4">
                <div>
                  <p className="font-bold">{w.name}</p>
                  <p className="text-xs text-muted-foreground">/site/{w.slug} · {w.type} · {w.products_count} products · {w.orders_count} orders</p>
                </div>
                <StatusBadge status={w.status} />
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
